/**
 * Product Gallery Component
 * Hero image gallery with thumbnail navigation and PhotoSwipe lightbox.
 * Handles image/video switching, counter updates, and lightbox triggers.
 */
class ProductGallery {
    constructor(container) {
        this.container = container;
        this.galleryId = container.getAttribute('data-gallery-id') || 'gallery';
        this.totalItems = parseInt(container.getAttribute('data-gallery-total') || '0', 10);

        this.mainImage = container.querySelector('.product-gallery__main-image');
        this.mainVideo = container.querySelector('.product-gallery__main-video');
        this.mainContainer = container.querySelector('.product-gallery__main');
        this.counter = container.querySelector('.product-gallery__counter [data-gallery-index]');
        this.thumbs = container.querySelectorAll('.product-gallery__thumb');
        this.arrows = {
            prev: container.querySelector('[data-action="prev"]'),
            next: container.querySelector('[data-action="next"]'),
        };
        this.tools = {
            zoom: container.querySelector('[data-action="zoom"]'),
            fullscreen: container.querySelector('[data-action="fullscreen"]'),
        };
        this.moreButton = container.querySelector('[data-action="open-all"]');

        // Extract JSON payload for PhotoSwipe
        const payloadScript = container.querySelector('[data-gallery-payload]');
        try {
            this.items = payloadScript ? JSON.parse(payloadScript.textContent) : [];
        } catch (e) {
            console.warn('Failed to parse gallery payload:', e);
            this.items = [];
        }

        this.currentIndex = 0;
        this.init();
    }

    init() {
        if (this.totalItems === 0) return;

        this.bindEvents();
        this.setActiveThumb(0);
        this.updateCounter();
        this.disableUnavailableTools();
    }

    disableUnavailableTools() {
        if (!this.isPhotoSwipeAvailable()) {
            if (this.tools.zoom) {
                this.tools.zoom.disabled = true;
                this.tools.zoom.classList.add('is-disabled');
                this.tools.zoom.setAttribute('aria-disabled', 'true');
            }
            if (this.tools.fullscreen) {
                this.tools.fullscreen.disabled = true;
                this.tools.fullscreen.classList.add('is-disabled');
                this.tools.fullscreen.setAttribute('aria-disabled', 'true');
            }
        }
    }

    bindEvents() {
        // Store handlers for cleanup in destroy()
        this._thumbHandlers = new Map();

        // Thumbnail click
        this.thumbs.forEach((thumb, index) => {
            const handler = () => this.selectMedia(index);
            this._thumbHandlers.set(thumb, handler);
            thumb.addEventListener('click', handler);
        });

        // Arrow navigation
        if (this.arrows.prev) {
            this._onPrevClick = () => this.previousMedia();
            this.arrows.prev.addEventListener('click', this._onPrevClick);
        }
        if (this.arrows.next) {
            this._onNextClick = () => this.nextMedia();
            this.arrows.next.addEventListener('click', this._onNextClick);
        }

        // Keyboard navigation
        this._onKeyDown = (e) => {
            if (e.key === 'ArrowLeft') {
                this.previousMedia();
            } else if (e.key === 'ArrowRight') {
                this.nextMedia();
            }
        };
        this.container.addEventListener('keydown', this._onKeyDown);

        // Tool buttons (disabled if PhotoSwipe not available)
        if (this.tools.zoom) {
            this._onZoomClick = () => {
                if (this.isPhotoSwipeAvailable()) {
                    this.openLightbox();
                }
            };
            this.tools.zoom.addEventListener('click', this._onZoomClick);
        }
        if (this.tools.fullscreen) {
            this._onFullscreenClick = () => {
                if (this.isPhotoSwipeAvailable()) {
                    this.openLightbox();
                }
            };
            this.tools.fullscreen.addEventListener('click', this._onFullscreenClick);
        }

        // Hero image click opens lightbox (if available)
        if (this.mainImage) {
            this._onMainImageClick = () => {
                if (this.isPhotoSwipeAvailable()) {
                    this.openLightbox();
                }
            };
            this.mainImage.addEventListener('click', this._onMainImageClick);
        }

        // "More" button opens lightbox at first hidden item (if available)
        if (this.moreButton) {
            this._onMoreClick = () => {
                if (this.isPhotoSwipeAvailable()) {
                    const firstHiddenIndex = 5;
                    this.openLightbox(firstHiddenIndex);
                }
            };
            this.moreButton.addEventListener('click', this._onMoreClick);
        }
    }

    selectMedia(index) {
        if (index === this.currentIndex || index < 0 || index >= this.totalItems) return;

        this.currentIndex = index;
        this.setActiveThumb(index);
        this.updateMainImage();
        this.updateCounter();
    }

    previousMedia() {
        const nextIndex = (this.currentIndex - 1 + this.totalItems) % this.totalItems;
        this.selectMedia(nextIndex);
    }

    nextMedia() {
        const nextIndex = (this.currentIndex + 1) % this.totalItems;
        this.selectMedia(nextIndex);
    }

    setActiveThumb(index) {
        this.thumbs.forEach((thumb, i) => {
            if (i === index) {
                thumb.classList.add('is-active');
                thumb.setAttribute('aria-selected', 'true');
            } else {
                thumb.classList.remove('is-active');
                thumb.setAttribute('aria-selected', 'false');
            }
        });
    }

    updateMainImage() {
        if (this.currentIndex >= this.items.length) return;

        const item = this.items[this.currentIndex];
        if (item.kind === 'video') {
            this.updateVideoDisplay(item);
        } else {
            this.updateImageDisplay(item);
        }
        this.mainContainer?.classList.add('is-loaded');
    }

    updateVideoDisplay(item) {
        if (!this.mainVideo) {
            // Hero markup only renders <video> when the first item is a video;
            // create it lazily so switching to a video thumb still works.
            this.mainVideo = document.createElement('video');
            this.mainVideo.className = 'product-gallery__main-video';
            this.mainContainer?.prepend(this.mainVideo);
        }
        this.mainVideo.style.display = 'block';
        if (this.mainImage) this.mainImage.style.display = 'none';

        this.mainVideo.setAttribute('controls', '');

        let source = this.mainVideo.querySelector('source');
        if (!source) {
            source = document.createElement('source');
            this.mainVideo.appendChild(source);
        }
        source.src = item.src;
        source.type = 'video/mp4';
        this.mainVideo.load();

        this.mainVideo.dataset.mediaIndex = this.currentIndex;
        this.mainVideo.dataset.mediaKind = item.kind;
    }

    updateImageDisplay(item) {
        if (!this.mainImage) {
            this.mainImage = document.createElement('img');
            this.mainImage.className = 'product-gallery__main-image';
            this.mainContainer?.prepend(this.mainImage);
        }
        this.mainImage.style.display = 'block';
        if (this.mainVideo) this.mainVideo.style.display = 'none';

        this.mainImage.src = item.src;
        this.mainImage.alt = item.alt;
        this.mainImage.width = item.w;
        this.mainImage.height = item.h;
        this.mainImage.dataset.mediaIndex = this.currentIndex;
        this.mainImage.dataset.mediaKind = item.kind;

        // Update <source> srcset inside <picture> element (for responsive image variants)
        const pictureElement = this.mainImage.closest('picture');
        if (pictureElement) {
            const sources = pictureElement.querySelectorAll('source');
            sources.forEach(source => {
                if (source.type === 'image/webp' && item.src) {
                    const srcset = this.generateResponsiveSrcset(item.src);
                    source.srcset = srcset;
                }
            });
        }

        // Trigger any lazy loading or image optimization if needed
        if (this.mainImage.complete === false) {
            this.mainImage.addEventListener('load', () => {
                this.mainContainer?.classList.add('is-loaded');
            }, { once: true });
        }
    }

    generateResponsiveSrcset(url) {
        const widths = [320, 480, 768, 1024, 1440, 1920];
        const baseUrl = url.substring(0, url.lastIndexOf('.'));

        return widths.map(width => {
            const variantUrl = `/generated/images${baseUrl}-${width}.webp`;
            return `${variantUrl} ${width}w`;
        }).join(', ');
    }

    updateCounter() {
        if (this.counter) {
            this.counter.textContent = String(this.currentIndex + 1).padStart(2, '0');
        }
    }

    isPhotoSwipeAvailable() {
        // Check for PhotoSwipe in multiple places to handle potential sandbox constraints
        return !!(window.PhotoSwipe || window.parent?.PhotoSwipe || globalThis?.PhotoSwipe);
    }

    openLightbox(startIndex = undefined) {
        // Gracefully degrade if PhotoSwipe is unavailable (e.g., SES sandbox)
        const PhotoSwipeLib = window.PhotoSwipe || window.parent?.PhotoSwipe || globalThis?.PhotoSwipe;

        if (!PhotoSwipeLib) {
            return;
        }

        const indexToOpen = startIndex !== undefined ? startIndex : this.currentIndex;
        const pswpElement = document.querySelector('.pswp');

        if (!pswpElement) {
            console.warn('PhotoSwipe container not found');
            return;
        }

        // Create and initialize PhotoSwipe instance
        const lightbox = new PhotoSwipeLib({
            dataSource: this.items,
            index: Math.max(0, Math.min(indexToOpen, this.items.length - 1)),
            bgOpacity: 0.85,
            wheelToZoom: true,
            pinchToZoom: true,
            showHideAnimationType: 'zoom',
            getNumItemsFn: () => this.items.length,
            getItemData: (index) => this.items[index],
        });

        // Sync gallery state when lightbox navigation occurs
        lightbox.on('change', () => {
            this.currentIndex = lightbox.currIndex;
            this.setActiveThumb(this.currentIndex);
            this.updateCounter();
        });

        lightbox.init();
    }

    destroy() {
        // Remove thumbnail handlers
        if (this._thumbHandlers) {
            this._thumbHandlers.forEach((handler, thumb) => {
                thumb.removeEventListener('click', handler);
            });
            this._thumbHandlers.clear();
        }

        // Remove arrow handlers
        if (this.arrows.prev && this._onPrevClick) {
            this.arrows.prev.removeEventListener('click', this._onPrevClick);
        }
        if (this.arrows.next && this._onNextClick) {
            this.arrows.next.removeEventListener('click', this._onNextClick);
        }

        // Remove keyboard handler
        if (this._onKeyDown) {
            this.container.removeEventListener('keydown', this._onKeyDown);
        }

        // Remove tool handlers
        if (this.tools.zoom && this._onZoomClick) {
            this.tools.zoom.removeEventListener('click', this._onZoomClick);
        }
        if (this.tools.fullscreen && this._onFullscreenClick) {
            this.tools.fullscreen.removeEventListener('click', this._onFullscreenClick);
        }

        // Remove hero image handler
        if (this.mainImage && this._onMainImageClick) {
            this.mainImage.removeEventListener('click', this._onMainImageClick);
        }

        // Remove more button handler
        if (this.moreButton && this._onMoreClick) {
            this.moreButton.removeEventListener('click', this._onMoreClick);
        }
    }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
    const gallery = document.querySelector('.product-gallery');
    if (gallery) {
        window.productGallery = new ProductGallery(gallery);

        if (window.XTronWebApp) {
            window.XTronWebApp.productGallery = window.productGallery;
        }
    }
});

// Re-initialize after the product-configuration-switcher swaps in a sibling product's content
document.addEventListener('xtron:product-content-swapped', (event) => {
    const root = event.detail && event.detail.root;
    const gallery = (root || document).querySelector('.product-gallery');
    if (!gallery) {
        return;
    }

    if (window.productGallery && typeof window.productGallery.destroy === 'function') {
        window.productGallery.destroy();
    }

    window.productGallery = new ProductGallery(gallery);
    if (window.XTronWebApp) {
        window.XTronWebApp.productGallery = window.productGallery;
    }
});

if (typeof module !== 'undefined' && module.exports) {
    module.exports = ProductGallery;
}
